import { Component } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { InternalHeaderComponent } from './internal-header/internal-header.component';
import { CommonModule } from '@angular/common';
import { ToastrModule } from 'ngx-toastr';
import { IdleTimeoutService } from './services/idle-timeout.service';
import { VendorShellComponent } from './shell/vendor-shell/vendor-shell.component';
import { StepUpReauthComponent } from './step-up-reauth/step-up-reauth.component';
import { getStoredUser } from './helpers/user-storage.util';
import { AliasBannerComponent } from './alias/alias-banner/alias-banner.component';
import { isPublicSiteVisitRespondUrl } from './site-visit/public-site-visit-route';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    InternalHeaderComponent,
    CommonModule,
    VendorShellComponent,
    AliasBannerComponent,
    StepUpReauthComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  constructor(
    public router: Router,
    private idleTimeoutService: IdleTimeoutService
  ) {}
  title = 'connect';

  // Public auth screens render with no shell. Matched on the path only (query
  // string / fragment stripped) so e.g. /resetpassword?token=... still matches.
  private static readonly AUTH_ROUTES = [
    '/login',
    '/createaccount',
    '/thanks',
    '/forgotpassword',
    '/resetpassword',
    // Opened from an emailed reset or invite link; the token is in the fragment.
    '/set-password',
  ];

  get isAuthRoute(): boolean {
    const path = this.router.url.split(/[?#]/)[0];
    return AppComponent.AUTH_ROUTES.includes(path);
  }

  /**
   * The site contact's no-login site-visit page. Bare outlet, like the auth screens — but matched on
   * a PREFIX rather than against {@link AUTH_ROUTES}, because the token is a path segment and an
   * exact-match list cannot hold a route with a parameter in it.
   *
   * 🔴 This is checked FIRST in the template, ahead of the internal branch, and the ordering is the
   * point: `isInternalUser` keys on the stored profile rather than on the route, so a signed-in
   * INTERNAL staffer opening a site contact's link would otherwise get the internal header wrapped
   * around an external page — and the vendor branch would fire vendor-only calls on behalf of someone
   * who is not the vendor. The page belongs to whoever holds the link, whatever session the browser
   * happens to be carrying.
   */
  get isPublicSiteVisitRoute(): boolean {
    return isPublicSiteVisitRespondUrl(this.router.url);
  }

  /** Every route that renders with no shell at all. */
  get isBareRoute(): boolean {
    return this.isAuthRoute || this.isPublicSiteVisitRoute;
  }

  get isInternalRoute(): boolean {
    return this.router.url.startsWith('/internal');
  }

  // Some authenticated routes (e.g. /manageaccount, /settings, /contact) are not
  // role-guarded and are reachable by both PA and INTERNAL users. isInternalRoute
  // alone would let an INTERNAL user on one of those paths fall through to the
  // vendor shell, which then fires vendor-only calls (e.g. topbar notifications).
  // Checking the stored user's role too keeps INTERNAL users on the internal
  // layout regardless of which shared route they're on.
  get isInternalUser(): boolean {
    return getStoredUser()?.role === 'INTERNAL';
  }
}
